import React, { useState } from 'react';
import {
  Alert,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Option,
  Select,
  Stack,
  Typography,
} from '@mui/joy';
import { Warning as WarningIcon } from '@phosphor-icons/react';
import { connectionApi, type ConnectionFormField } from '../../lib/distributorsApi';

interface Props {
  distributorId: number;
  displayName: string;
  accountNoun?: string;
  fields: ConnectionFormField[];
  /** Extra values merged into the payload (e.g. geocoded coordinates). */
  hiddenValues?: Record<string, unknown>;
  onConnected: () => void;
}

/**
 * Generic connect form for distributors whose API has no OAuth redirect.
 *
 * The field list is supplied by the backend provider (connection_form), so a
 * new vendor needs no React changes. Secrets live in component state only
 * until submit and are never persisted or echoed back by the API.
 */
export default function DistributorCredentialsForm({
  distributorId,
  displayName,
  accountNoun,
  fields,
  hiddenValues,
  onConnected,
}: Props) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    fields.forEach((field) => {
      initial[field.name] = field.type === 'select' ? (field.options?.[0]?.value ?? '') : '';
    });
    return initial;
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const visibleFields = fields.filter((field) => field.type !== 'hidden');
  const missingRequired = fields.some(
    (field) => field.required && !values[field.name] && !(hiddenValues || {})[field.name],
  );

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (submitting || missingRequired) return;
    setSubmitting(true);
    setError(null);
    setFieldErrors({});
    try {
      await connectionApi.connectWithCredentials(distributorId, {
        ...values,
        ...(hiddenValues || {}),
      });
      // Drop the secrets from memory as soon as the exchange succeeds.
      setValues((current) => {
        const cleared = { ...current };
        fields.forEach((field) => {
          if (field.type === 'password') cleared[field.name] = '';
        });
        return cleared;
      });
      onConnected();
    } catch (err: any) {
      const data = err?.response?.data ?? err?.responseData ?? {};
      if (data && typeof data === 'object' && !data.error && !data.detail) {
        const perField: Record<string, string> = {};
        Object.entries(data).forEach(([key, value]) => {
          if (fields.some((field) => field.name === key)) {
            perField[key] = Array.isArray(value) ? String(value[0]) : String(value);
          }
        });
        if (Object.keys(perField).length) setFieldErrors(perField);
      }
      setError(data.error || data.detail || err?.message || 'Could not connect the account');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div onKeyDown={event => { if (event.key === 'Enter') void handleSubmit(event); }}>
      <Stack spacing={2}>
        <Typography level="body-sm" color="neutral">
          Enter the {accountNoun || `${displayName} account`} details to connect. Your
          credentials are sent straight to {displayName} to authorize this
          connection — ApexOS does not store your password.
        </Typography>

        {error && (
          <Alert color="danger" variant="soft" startDecorator={<WarningIcon />}>
            {error}
          </Alert>
        )}

        {visibleFields.map((field) => (
          <FormControl key={field.name} required={field.required} error={!!fieldErrors[field.name]}>
            <FormLabel>{field.label}</FormLabel>
            {field.type === 'select' ? (
              <Select
                value={values[field.name] ?? ''}
                onChange={(_event, value) =>
                  setValues((current) => ({ ...current, [field.name]: (value as string) ?? '' }))
                }
              >
                {(field.options || []).map((option) => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            ) : (
              <Input
                type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                value={values[field.name] ?? ''}
                autoComplete={field.type === 'password' ? 'off' : undefined}
                onChange={(event) =>
                  setValues((current) => ({ ...current, [field.name]: event.target.value }))
                }
              />
            )}
            {(fieldErrors[field.name] || field.help_text) && (
              <FormHelperText>{fieldErrors[field.name] || field.help_text}</FormHelperText>
            )}
          </FormControl>
        ))}

        <Button type="button" onClick={handleSubmit} loading={submitting} disabled={missingRequired}>
          Connect {displayName}
        </Button>
      </Stack>
    </div>
  );
}
